import React, { useState, useEffect, useMemo } from 'react';
import Swal from 'sweetalert2';
import { Table, TableBody, TableCell, TableHead, TableRow, Paper, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../helper/EstimatedShippingDate.helper';
import { EstimatedDateGridProps, RowData } from '../../helper/EstimatedShippingDate.types';
import Helper from '../../../../utils/helper';
import { ESDGRID } from '../../estimatedShippingDate.classes';

export const EstimatedDateGrid: React.FC<EstimatedDateGridProps> = ({
    data,
    backlog,
    onUpdateRow,
    onGridError,
    setUpdatedData,
}) => {
    const { t } = useTranslation();
    const [errors, setErrors] = useState<boolean[]>([]);

    const validateWarehouseForecast = (salesForecast: number, warehouseExitForecast: number, backlogToCalc: any) => {
        const maxAllowedValue = salesForecast + backlogToCalc;
        return warehouseExitForecast > maxAllowedValue;
    };

    const handleInputChange =
        (field: keyof RowData, index: number, calculatedData: any) => (event: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = event.target.value.replace(/[^0-9]/g, '');
            if (newValue.length > 6) {
                Swal.fire({
                    icon: 'error',
                    title: t('general.error'),
                    html: `<b>${t('cargoManagement.estimatedShippingDate.inputMaxValue')}</b>`,
                });
                return;
            }

            const updatedRow: RowData = { ...data[index], [field]: newValue };
            const newErrors = [...errors];

            const numericValue = newValue === '' ? 0 : Number(newValue);
            const salesForecast = field === 'salesForecast' ? numericValue : updatedRow.salesForecast || 0;
            const warehouseExitForecast =
                field === 'warehouseExitForecast' ? numericValue : updatedRow.warehouseExitForecast || 0;

            const calcBacklog = index === 0 ? backlog : calculatedData?.[index - 1]?.estimatedEndOfDayBacklog;

            newErrors[index] = field !== 'warehouseExitForecast' && Number(numericValue) === 0;

            if (validateWarehouseForecast(Number(salesForecast), Number(warehouseExitForecast), calcBacklog)) {
                Swal.fire({
                    icon: 'error',
                    title: t('general.error'),
                    html: `<b>${t('cargoManagement.estimatedShippingDate.valueExceedsCombinedLimit')}</b>`,
                });
                updatedRow.warehouseExitForecast = 0;
                newErrors[index] = true;
            }

            setErrors(newErrors);
            onUpdateRow(updatedRow);
            setUpdatedData((prev: any) => {
                return {
                    ...prev,
                    [updatedRow.date]: updatedRow,
                };
            });
        };

    const useCalculatedBacklog = useMemo(() => {
        return (data || []).reduce((acc: any, row: any, index: any) => {
            const salesForecast = Number(row.salesForecast);
            const warehouseExitForecast = Number(row.warehouseExitForecast);

            if (isNaN(salesForecast) || isNaN(warehouseExitForecast)) {
                acc.push({
                    ...row,
                    estimatedEndOfDayBacklog: 0,
                    calculatedEstimatedOutcomeDay: 0,
                });
                return acc;
            }

            const previousBacklog = index === 0 ? backlog : acc[index - 1]?.estimatedEndOfDayBacklog;
            const estimatedEndOfDayBacklog = previousBacklog + salesForecast - warehouseExitForecast;
            const calculatedEstimatedOutcomeDay =
                warehouseExitForecast !== 0 ? (previousBacklog + salesForecast) / warehouseExitForecast : 0;

            acc.push({
                ...row,
                estimatedEndOfDayBacklog,
                calculatedEstimatedOutcomeDay,
            });

            return acc;
        }, []);
    }, [data, backlog]);

    useEffect(() => {
        onGridError(errors.some((error) => error));
    }, [errors, onGridError]);

    return (
        <Paper className={ESDGRID.paper} elevation={3} sx={{ padding: '16px', borderRadius: '8px' }}>
            <Table className={ESDGRID.table}>
                <TableHead>
                    <TableRow className={ESDGRID.headerRow} sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell style={{ textAlign: 'center', fontWeight: 'bold' }}>{t('general.date')}</TableCell>
                        <TableCell style={{ textAlign: 'center', fontWeight: 'bold' }}>
                            {t('cargoManagement.estimatedShippingDate.salesForecast')}
                        </TableCell>
                        <TableCell style={{ textAlign: 'center', fontWeight: 'bold' }}>
                            {t('cargoManagement.estimatedShippingDate.warehouseExitForecast')}
                        </TableCell>
                        <TableCell style={{ textAlign: 'center', fontWeight: 'bold' }}>
                            {t('cargoManagement.estimatedShippingDate.calculatedEstimatedOutcomeDay')}
                        </TableCell>
                        <TableCell style={{ textAlign: 'center', fontWeight: 'bold' }}>
                            {t('cargoManagement.estimatedShippingDate.estimatedEndOfDayBacklog')}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {(useCalculatedBacklog || [])?.map((row: any, index: any) => (
                        <TableRow key={index} className={ESDGRID.bodyRow}>
                            <TableCell sx={{ textAlign: 'center' }}>{formatDate(row.date)}</TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                                <TextField
                                    className={ESDGRID.inputTextfieldSalesForecast}
                                    value={
                                        row.salesForecast === '' ? '' : Helper.formatNumberWithDots(row.salesForecast)
                                    }
                                    onChange={handleInputChange('salesForecast', index, useCalculatedBacklog)}
                                    size="small"
                                    disabled={row.isDisabled}
                                    error={
                                        (row.salesForecast === '' ? false : Number(row.salesForecast) === 0) ||
                                        errors[index]
                                    }
                                    slotProps={{
                                        htmlInput: {
                                            inputMode: 'numeric',
                                            pattern: '[0-9]*',
                                        },
                                    }}
                                    sx={{ width: '100px', '& input': { textAlign: 'center' } }}
                                />
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                                <TextField
                                    className={ESDGRID.inputTextfieldWarehouseExit}
                                    value={
                                        row.warehouseExitForecast === ''
                                            ? ''
                                            : Helper.formatNumberWithDots(row.warehouseExitForecast)
                                    }
                                    onChange={handleInputChange('warehouseExitForecast', index, useCalculatedBacklog)}
                                    size="small"
                                    disabled={row.isDisabled}
                                    slotProps={{
                                        htmlInput: {
                                            inputMode: 'numeric',
                                            pattern: '[0-9]*',
                                        },
                                    }}
                                    error={
                                        errors[index] ||
                                        validateWarehouseForecast(
                                            Number(row.salesForecast),
                                            Number(row.warehouseExitForecast),
                                            Number(row.estimatedEndOfDayBacklog)
                                        )
                                    }
                                    sx={{ width: '100px', '& input': { textAlign: 'center' } }}
                                />
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                                {isNaN(row.calculatedEstimatedOutcomeDay)
                                    ? 0
                                    : (Math.round(row.calculatedEstimatedOutcomeDay * 10) / 10).toFixed(1)}
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                                {Helper.formatNumberWithDots(
                                    isNaN(row.estimatedEndOfDayBacklog)
                                        ? 0
                                        : Math.round(row.estimatedEndOfDayBacklog * 1000) / 1000
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Paper>
    );
};
